import * as React from 'react';
import {
  Box,
  Button,
  ClickAwayListener,
  Grid,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Stack,
  Link,
  Toolbar,
  Tooltip,
  Typography, 
  IconButton
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useContext, useEffect, useState } from "react";
import { Circle, KeyboardArrowDown, LinkedIn, Public } from "@mui/icons-material";
import { DataGridPro, GridOverlay } from '@mui/x-data-grid-pro';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import "./styles.css";
import { getCompanies } from "../../services/Companies";
import AuthContext from "../../context/AuthContext";
import SnackbarContext from "../../context/SnackbarContext";
import { getContacts } from "../../services/Contacts";
import { executeJob } from '../../services/Superuser';
import theme from "../../theme";

const noRowsTextActive =
  "Companies will be added here as actions consistent with defined Motivation Signals are detected.";
const noRowsTextInactive =
  "This model is inactive. To begin tracking defined Motivation Signals, select Launch Model on the Edit page.";
const newCompanyTooltip =
  "This company started registering purchase motivation within the past two weeks.";
const daysNewThreshold = 7;

const pastels = [
  "#ff968a",
  "#56cbce",
  "#ecd6e3",
  "#f6ebc2",
  "#fdd7c2",
  "#ffe1e9",
  "#c7dcda",
  "#f3b0c2",
  "#ffcdb6",
  "#cbaacb",
  "#acdee7",
];

const useStyles = makeStyles({
  newCompany: {
    "&.MuiSvgIcon-root": {
      fontSize: "10px",
      position: "absolute",
      marginLeft: "-40px",
    },
  },
  roundedLogo: {
    objectFit: "cover",
    borderRadius: "50%",
    width: "32px",
    height: "32px",
    [theme.breakpoints.down('xl')]: {
      width: "26.67px",
      height: "26.67px",
    },
    marginLeft: "18px",
    marginRight: "10px"
  },
  roundedLogoLetter: {
    width: "38px",
    height: "38px",
    [theme.breakpoints.down('xl')]: {
      width: "32px",
      height: "32px",
    },
    marginLeft: "18px",
    marginRight: "10px"
  },
  logoLetter: {
    color: "#fdfdfd",
    position: "absolute",
    fontSize: "22px",
    [theme.breakpoints.down('xl')]: {
      fontSize: "18px",
    },
    marginLeft: "18px",
    marginRight: "10px",
    textAlign: "center",
  },
  headerBox: {
    color: "#95a2b4",
    fontSize: "13px",
    fontWeight: "bold",
  },
  dataGridBox: {
    background: "#fdfdfd",
    borderRadius: "3px",
  },
  dataGrid: {
    color: "#3d4849",
    "&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus, &.MuiDataGrid-root .MuiDataGrid-cell:focus":
      {
        outline: "none",
      }
  },
  domainLink: {
    color: "#3d4849",
    "&:hover": {
      color: "#009cf8",
    },
  },
});

const acceptableLogoSize = (w, h) => {
  if (h === 0) return false;
  const ratio = w / h;
  if (ratio >= 0.74 && ratio <= 1.4) return true;
  return false;
};

function CompanyLinkIcon({variant, url}) {
  if (!url) return (
    <React.Fragment>
      <IconButton
        aria-label="company website link"
        color="primary"
        disabled
      >
        {variant === 'linkedin' ? <LinkedIn /> : <Public />}
      </IconButton>
    </React.Fragment>
  )
  
  return (
    <React.Fragment>
      <Link
        href={url}
        target="_blank"
        rel="noreferrer"
      >
        <IconButton
          aria-label="company website link"
          color="primary"
        >
          {variant === 'linkedin' ? <LinkedIn /> : <Public />}
        </IconButton>
      </Link>
    </React.Fragment>
  )
}

const createMotivationSignalsObj = (signals) => {
  let motivation_signals = {
    webpage_visits: new Set(),
    custom_signals: new Set(),
    competitor_interest: new Set(),
    keywords: new Set(),
    events_and_webinars: new Set(),
    functional_investment: new Set(),
  };

  signals.forEach(signal => {
    if (signal.type === 'Website Page') {motivation_signals.webpage_visits.add(signal.value)};
    if (signal.type === 'Custom Signal') {motivation_signals.custom_signals.add(signal.value)};
    if (signal.type === 'Competitor Interest') {motivation_signals.competitor_interest.add(signal.value)};
    if (signal.type === 'Keyword') {motivation_signals.keywords.add(signal.value)};
    if (signal.type === 'Event and Webinar') {motivation_signals.events_and_webinars.add(signal.value)};
    if (signal.type === 'Functional Investment') {motivation_signals.functional_investment.add(signal.value)};
  });

  return motivation_signals
}

const getMotivationSignalsString = (signals, revenueTargetingModelID) => {
  let motivation_signals = createMotivationSignalsObj(signals);

  let result = '';
  for (const [key, value] of Object.entries(motivation_signals)) {
    if (value.size > 0) {
      if (result !== '') {result += ', '};
      result += [...value].join(', ');
    }
  };
  
  if (result === '') {
    if (revenueTargetingModelID === 106 || revenueTargetingModelID === 107) {
      return ''
    }
    return 'RevScan';
  } else {
    return result;
  }
}

const getMotivationSignalsFragment = (signals) => {
  const headerStyle = {
    fontWeight: ""
  }

  const olStyle = {
    marginTop: 2,
    marginBottom: 4,
    paddingLeft: 30
  }
  const liStyle = {
    fontSize: 12
  }
  let motivation_signals = createMotivationSignalsObj(signals);

  function titleCase(str) {
    return str.toLowerCase().split('_').map(function(word) {
      return word.replace(word[0], word[0].toUpperCase());
    }).join(' ');
  }

  return (
    <React.Fragment>
      {Object.entries(motivation_signals).map(([key, value]) => (
        <>
          {value.size > 0 &&
            <>
              <Typography style={headerStyle} key={key}>{titleCase(key)}</Typography>
                <ol style={olStyle}>
                  {[...value].map((item) => (
                    <li style={liStyle} key={item}>{item}</li>
                  ))}
                </ol>
            </>
          }
        </>
      ))}
    </React.Fragment>
  );
}

export default function CompanyList({ revenueTargetingModelID, activeModel }) {
  const [isLoading, setIsLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [exportURL, setExportURL] = useState("");
  const [exportContactsURL, setExportContactsURL] = useState("");
  const [exportContactsLoading, setExportContactsLoading] = useState(false);
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(0);
  const [rowCount, setRowCount] = useState(0);
  const [executeOpen, setExecuteOpen] = React.useState(false);
  const executeAnchorRef = React.useRef(null);
  const [exportOpen, setExportOpen] = React.useState(false);
  const exportAnchorRef = React.useRef(null);

  const auth = useContext(AuthContext);

  const snackbar = useContext(SnackbarContext);

  const classes = useStyles();

  const tableBlurSX = {filter: "blur(4px)", userSelect: "None"};

  const handleExecuteToggle = () => {
    setExecuteOpen((prevOpen) => !prevOpen);
  };

  const handleExecuteClose = (event) => {
    if (executeAnchorRef.current && executeAnchorRef.current.contains(event.target)) {
      return;
    }
    setExecuteOpen(false);
  };

  function handleExecuteListKeyDown(event) {
    if (event.key === 'Tab') {
      event.preventDefault();
      setExecuteOpen(false);
    } else if (event.key === 'Escape') {
      setExecuteOpen(false);
    }
  }

  const handleExportToggle = () => {
    setExportOpen((prevOpen) => !prevOpen);
  };

  const handleExportClose = (event) => {
    if (exportAnchorRef.current && exportAnchorRef.current.contains(event.target)) {
      return;
    }
    setExportOpen(false);
  };

  function handleExportListKeyDown(event) {
    if (event.key === 'Tab') {
      event.preventDefault();
      setExportOpen(false);
    } else if (event.key === 'Escape') {
      setExportOpen(false);
    }
  }

  const onExecute = (type) => {
    console.log('execute job: ', type);
    executeJob(auth.token, revenueTargetingModelID, type)
      .then((response) => {
        if (response.ok) {
          response.json()
            .then((json_response) => {
              snackbar.createSuccess(json_response.message)
            })
        } else {
          response.json()
            .then((json_response) => {
              snackbar.createError(json_response.message)
            })
        }
      })
      .catch((error) => {
        snackbar.createError("Error executing job.");
      })
  }

  useEffect(() => {
    let active = true;

    const fetchData = async () => {
      if (revenueTargetingModelID) {
        setIsLoading(true);
        setCompanies([]);
        setExportURL("");
        getCompanies(auth.token, revenueTargetingModelID, pageSize, page + 1)
          .then((response) => {
            if (active === true) {
              if (response.companies) {
                setCompanies(response.companies);
                if (response.export_url) {
                  setExportURL(response.export_url);
                }
                setRowCount(response.total_objects);
              } else {
                throw new Error("invalid response from server: companies");
              }
            }
          })
          .catch((error) => {
            snackbar.createError("Error fetching companies.");
          })
          .finally(() => {
            setIsLoading(false);
          });
      }
    };

    // fixes race condition
    fetchData();
    return () => {
      active = false;
    };
  }, [revenueTargetingModelID, page, pageSize]);

  useEffect(() => {
    setExportContactsLoading(true);
    getContacts(auth.token, revenueTargetingModelID)
      .then((response) => {
        if (response.export_url) {
          setExportContactsURL(response.export_url);
        } else {
          setExportContactsURL("");
        }
      })
      .catch((error) => {
        snackbar.createError("Error retrieving contact export URL.");
      })
      .finally(() => {
        setExportContactsLoading(false);
      });
  }, [auth.userAccountID, revenueTargetingModelID]);

  const thresholdDate = new Date();
  thresholdDate.setDate(thresholdDate.getDate() - daysNewThreshold);

  const theme = useTheme();
  const belowXlScreen = useMediaQuery(theme.breakpoints.down("xl"));
  const xlAndAboveScreen = useMediaQuery(theme.breakpoints.up("xl"));
  
  const rowHeight = belowXlScreen ? 42: xlAndAboveScreen ? 50: 50
  const columnWidths = {
    logo: belowXlScreen ? 70: xlAndAboveScreen ? 80: 80,
    name: belowXlScreen ? 240: xlAndAboveScreen ? 300: 300,
    domain: belowXlScreen ? 110: xlAndAboveScreen ? 120: 120,
    rev_score: belowXlScreen ? 104: xlAndAboveScreen ? 120: 120,
    motivation_signals: belowXlScreen ? 170: xlAndAboveScreen ? 210: 210,
    city: belowXlScreen ? 110: xlAndAboveScreen ? 140: 140,
    state: belowXlScreen ? 110: xlAndAboveScreen ? 140: 140,
    country: belowXlScreen ? 110: xlAndAboveScreen ? 140: 140,
    business_type: belowXlScreen ? 125: xlAndAboveScreen ? 140: 140,
    industry_group: belowXlScreen ? 240: xlAndAboveScreen ? 260: 260,
    employee_band: belowXlScreen ? 125: xlAndAboveScreen ? 160: 160,
    revenue_band: belowXlScreen ? 125: xlAndAboveScreen ? 160: 160
  }

  const columns = [
    {
      field: "name",
      headerName: "Company",
      width: columnWidths.name,
      renderCell: (params) => {
        const startDate = new Date(params.row.matched_datetime);

        return (
          <Grid container spacing={0} alignItems="center" sx={params.row.domain?.slice(0, 2)==='XX' && tableBlurSX}>
            <Grid item xs={3} alignItems="center" display="flex" justifyContent="center">
              {thresholdDate.getTime() < startDate.getTime() && (
                <Tooltip title={newCompanyTooltip} arrow placement="bottom-start">
                  <Circle className={classes.newCompany} color="primary" />
                </Tooltip>
              )}
              {acceptableLogoSize(
                params.row.logo_width,
                params.row.logo_height
              ) ? (
                <img
                  className={classes.roundedLogo}
                  src={params.row.logo_url}
                  alt={params.row.name}
                />
              ) : (
                <>
                  <Circle
                    className={classes.roundedLogoLetter}
                    sx={{
                      color:
                        pastels[
                          params.row.name.length > 1
                            ? params.row.name.charCodeAt(0) % pastels.length
                            : 0
                        ],
                    }}
                  />
                  <div className={classes.logoLetter}>{params.row.name[0]}</div>
                </>
              )}
            </Grid>
            <Grid item xs={9}>
              <strong>{params.value}</strong> 
            </Grid>
          </Grid>
        );
      },
    },
    {
      field: "domain",
      headerName: "Links",
      width: columnWidths.domain,
      sortable:false,
      renderCell: (params) => {
        return (
          <>
            {params.row.domain?.slice(0, 2)!=='XX' ? (
              <>
                <CompanyLinkIcon 
                  url={params.value}
                />
                <CompanyLinkIcon 
                  url={params.row.linkedin_url}
                  variant="linkedin"
                />
              </>
            ) : (
              <>
                <CompanyLinkIcon 
                  url={undefined}
                />
                <CompanyLinkIcon 
                  url={undefined}
                  variant="linkedin"
                />
              </>
            )}
          </>
        )
      }
    },
    {
      field: "rev_score",
      headerName: revenueTargetingModelID === 71 ? "Prospect Score" : revenueTargetingModelID === 106 ? "Churn Risk" : revenueTargetingModelID === 107 ? "Upsell Score": "RevScore",
      align: "left",
      width: revenueTargetingModelID === 71 ? 140 : columnWidths.rev_score,
      renderCell: (params) => (<Box sx={params.row.domain?.slice(0, 2)==='XX' && tableBlurSX}>{params.value}</Box>)
    },
    {
      field: "motivation_signals",
      headerName: "Motivation Signals",
      align: "left",
      width: columnWidths.motivation_signals,
      sortComparator: (v1, v2) => {
        if (getMotivationSignalsString(v1).toLowerCase() > getMotivationSignalsString(v2).toLowerCase()) {
          return 1;
        } else {
          return -1;
        }
      },
      renderCell: (params) => (
        params.row.domain?.slice(0, 2)==='XX' ? (
          <Box sx={tableBlurSX}>
            <Box sx={{color:theme.palette.primary.main, width: `${columnWidths.motivation_signals-30}px`, overflow:"hidden", display: "block", whiteSpace: "nowrap",textOverflow: "ellipsis"}}>
              {getMotivationSignalsString(params.value, revenueTargetingModelID)}
            </Box>
          </Box>
        ) : (
          getMotivationSignalsString(params.value) !== 'RevScan' ? (
            <Tooltip arrow title={getMotivationSignalsFragment(params.value)}>
              <Box sx={{userSelect: "none", color:theme.palette.primary.main, width: `${columnWidths.motivation_signals-30}px`, overflow:"hidden", display: "block", whiteSpace: "nowrap",textOverflow: "ellipsis"}}>
                {getMotivationSignalsString(params.value, revenueTargetingModelID)}
              </Box>
            </Tooltip>
          ) : (
            <Box sx={{userSelect: "none", color:theme.palette.primary.main, width: `${columnWidths.motivation_signals-30}px`, overflow:"hidden", display: "block", whiteSpace: "nowrap",textOverflow: "ellipsis"}}>
              {getMotivationSignalsString(params.value, revenueTargetingModelID)}
            </Box>
          )
          
        )
      )
    },
    {
      field: "city",
      headerName: "City",
      width: columnWidths.city,
      renderCell: (params) => (<Box sx={params.row.domain?.slice(0, 2)==='XX' && tableBlurSX}>{params.value}</Box>)
    },
    {
      field: "state",
      headerName: "State",
      width: columnWidths.state,
      renderCell: (params) => (<Box sx={params.row.domain?.slice(0, 2)==='XX' && tableBlurSX}>{params.value}</Box>)
    },
    {
      field: "country",
      headerName: "Country",
      width: columnWidths.country,
      renderCell: (params) => (<Box sx={params.row.domain?.slice(0, 2)==='XX' && tableBlurSX}>{params.value}</Box>)
    },
    {
      field: "business_type",
      noWrap: true,
      headerName: "Business Type",
      width: columnWidths.business_type,
      renderCell: (params) => (<Box sx={params.row.domain?.slice(0, 2)==='XX' && tableBlurSX}>{params.value}</Box>)
    },
    {
      field: "industry_group",
      noWrap: true,
      headerName: "Industry Group",
      width: columnWidths.industry_group,
      renderCell: (params) => (<Box sx={params.row.domain?.slice(0, 2)==='XX' && tableBlurSX}>{params.value}</Box>)
    },
    {
      field: "employee_band",
      headerName: "Employees",
      width: columnWidths.employee_band,
      sortComparator: (v1, v2) => parseInt(v1) - parseInt(v2),
      renderCell: (params) => (<Box sx={params.row.domain?.slice(0, 2)==='XX' && tableBlurSX}>{params.value}</Box>)
    },
    {
      field: "revenue_band",
      headerName: "Revenue",
      width: columnWidths.revenue_band,
      sortComparator: (v1, v2) => {
        if (v1 === "$1B+") {
          v1 = "$1000M"
        }
        if (v2 === "$1B+") {
          v2 = "$1000M"
        }
        return parseInt(v1.replace("$", "")) - parseInt(v2.replace("$", ""))
      },
      renderCell: (params) => (<Box sx={params.row.domain?.slice(0, 2)==='XX' && tableBlurSX}>{params.value}</Box>)
    }
  ];

  const onExportCompanies = () => {
    const link = document.createElement("a");
    link.download = exportURL;
    link.href = exportURL;
    link.click();
  };

  const onExportContacts = () => {
    const link = document.createElement("a");
    link.download = exportContactsURL;
    link.href = exportContactsURL;
    link.click();
  };

  const NoRowsOverlay = () => {
    return (
      <GridOverlay>
        <Stack alignItems="center" justifyContent="center" spacing={2}>
          {activeModel ? noRowsTextActive : noRowsTextInactive}
        </Stack>
      </GridOverlay>
    );
  };

  const NoResultsOverlay = () => {
    return (
      <GridOverlay>
        <Stack alignItems="center" justifyContent="center" spacing={2}>
          {activeModel ? noRowsTextActive : noRowsTextInactive}
        </Stack>
      </GridOverlay>
    );
  };

  return (
    <>
      <Toolbar sx={{ padding: "0 !important" }}>
        <Box className={classes.headerBox} flex={1}>
          {[106, 107].includes(revenueTargetingModelID) ? 'Accounts monitored:' : 'In-market companies:'}
          <br />
          <span style={{ color: "#009cf8", fontSize: "18px" }}>{rowCount}</span>
        </Box>
        {auth.superUser && (
          <Box sx={{zIndex:10}}>
            <Button
              ref={executeAnchorRef}
              id="execute-button"
              variant="contained"
              color="primary"
              sx={{ width: 160, fontWeight: "bold" }}
              aria-controls={executeOpen ? 'execute-menu' : undefined}
              aria-expanded={executeOpen ? 'true' : undefined}
              aria-haspopup="true"
              onClick={handleExecuteToggle}
            >
              <Box flex={1}>
                Execute
              </Box>
              <KeyboardArrowDown />
            </Button>
            <Popper
              open={executeOpen}
              anchorEl={executeAnchorRef.current}
              role={undefined}
              placement="bottom"
              transition
              disablePortal
            >
              {({ TransitionProps, placement }) => (
                <Grow
                  {...TransitionProps}
                  style={{
                    transformOrigin:
                      placement === 'bottom-start' ? 'left top' : 'left bottom',
                  }}
                >
                  <Paper sx={{width:160}}>
                    <ClickAwayListener onClickAway={handleExecuteClose}>
                      <MenuList
                        autoFocusItem={executeOpen}
                        id="execute-menu"
                        aria-labelledby="execute-button"
                        onKeyDown={handleExecuteListKeyDown}
                      >
                        <MenuItem onClick={handleExecuteClose}>
                          <Button
                            variant="outlined"
                            color="primary"
                            sx={{ width: 1/1, fontWeight: "bold" }}
                            onClick={() => onExecute('company')}
                          >
                            Companies
                          </Button>
                        </MenuItem>
                        <MenuItem onClick={handleExecuteClose}>
                          <Button
                            variant="outlined"
                            color="primary"
                            sx={{ width: 1/1, fontWeight: "bold" }}
                            onClick={() => onExecute('contact')}
                          >
                            Contacts
                          </Button>
                        </MenuItem>
                      </MenuList>
                    </ClickAwayListener>
                  </Paper>
                </Grow>
              )}
            </Popper>
          </Box>
        )}
        <Box sx={{zIndex:10}}>
          <Button
            ref={exportAnchorRef}
            id="export-button"
            variant="contained"
            color="primary"
            sx={{ width:160, fontWeight: "bold", ml: "12px" }}
            aria-controls={exportOpen ? 'export-menu' : undefined}
            aria-expanded={exportOpen ? 'true' : undefined}
            aria-haspopup="true"
            onClick={handleExportToggle}
          >
            <Box flex={1}>
              Export
            </Box>
            <KeyboardArrowDown />
          </Button>
          <Popper
            open={exportOpen}
            anchorEl={exportAnchorRef.current}
            role={undefined}
            placement="bottom"
            transition
            disablePortal
          >
            {({ TransitionProps, placement }) => (
              <Grow
                {...TransitionProps}
                style={{
                  transformOrigin:
                    placement === 'bottom-start' ? 'left top' : 'left bottom',
                }}
              >
                <Paper sx={{width:160}}>
                  <ClickAwayListener onClickAway={handleExportClose}>
                    <MenuList
                      autoFocusItem={executeOpen}
                      id="export-menu"
                      aria-labelledby="export-button"
                      onKeyDown={handleExportListKeyDown}
                    >
                      <MenuItem onClick={handleExportClose}>
                        <Button
                          variant="outlined"
                          color="primary"
                          disabled={exportURL === ""}
                          sx={{ width: 1/1, fontWeight: "bold" }}
                          onClick={onExportCompanies}
                        >
                          Companies
                        </Button>
                      </MenuItem>
                      <MenuItem onClick={handleExportClose}>
                        <Button
                          loading={exportContactsLoading}
                          variant="outlined"
                          color="primary"
                          disabled={exportContactsURL === ""}
                          sx={{ width: 1/1, fontWeight: "bold" }}
                          onClick={onExportContacts}
                        >
                          Contacts
                        </Button>
                      </MenuItem>
                    </MenuList>
                  </ClickAwayListener>
                </Paper>
              </Grow>
            )}
          </Popper>
        </Box>
      </Toolbar>
      <Box 
        className={classes.dataGridBox} 
        height={auth.userAccount.tier.name.includes("Trial") ? "calc(100vh - 192px)" : "calc(100vh - 152px)"}
        zIndex={1}
      >
        <DataGridPro
          loading={isLoading}
          components={{
            NoRowsOverlay,
            NoResultsOverlay
          }}
          density="standard"
          disableColumnMenu
          disableSelectionOnClick
          disableColumnSelector
          getRowId={(row) => row.id}
          pagination
          paginationMode="server"
          page={page}
          onPageChange={(pg) => setPage(pg)}
          pageSize={pageSize}
          onPageSizeChange={(size) => setPageSize(size)}
          rowHeight={rowHeight}
          rowCount={rowCount}
          rows={companies}
          columns={columns}
          className={classes.dataGrid}
          initialState={{ pinnedColumns: { left: ["name"] } }}
          sx={{
            fontSize: {xs:'0.75rem', sm:'0.75rem', md:'0.75rem', lg:'0.75rem', xl: "0.875rem"}
          }}
        />
      </Box>
    </>
  );
}
